// ============================================================
// DockerfileDeepDive.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: A Dockerfile is a text document containing all
//   commands a user could call on the command line to assemble an image.
// - Strategy: Focus on instruction "Keywords" and their roles.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import DescriptionIcon from "@mui/icons-material/Description";
import TerminalIcon from "@mui/icons-material/Terminal";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import StorageIcon from "@mui/icons-material/Storage";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TERMINAL_BG = "#011627";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const InstructionCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: "rgba(255,255,255,0.02)",
    borderLeft: `4px solid ${DOCKER_BLUE}`,
    borderRadius: "0 8px 8px 0",
    height: "100%",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(13, 183, 237, 0.05)",
        transform: "translateY(-5px)"
    }
}));

const TerminalWindow = styled(Paper)(({ theme }) => ({
    backgroundColor: TERMINAL_BG,
    padding: theme.spacing(4),
    borderRadius: "12px",
    border: `1px solid ${BRAND.border}`,
    fontFamily: "'Fira Code', monospace",
    boxShadow: "0 20px 40px rgba(0,0,0,0.4)"
}));

export default function DockerfileDeepDive() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Development · Module 06"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Dockerfile <span style={{ color: DOCKER_BLUE }}>Deep-Dive</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        The Dockerfile is the automation script for your infrastructure.
                        Every line is a step toward building a perfect, portable environment.
                    </Typography>
                </Box>

                {/* 2. THE ANATOMY OF A DOCKERFILE */}
                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={7}>
                        <TerminalWindow>
                            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", mb: 2, display: 'block' }}>
                                # Example: Node.js Web App
                            </Typography>

                            <Typography sx={{ color: "#82AAFF", mb: 1 }}>
                                <span style={{ color: "#C792EA", fontWeight: 'bold' }}>FROM</span> node:18-alpine
                            </Typography>

                            <Typography sx={{ color: "#82AAFF", mb: 1 }}>
                                <span style={{ color: "#C792EA", fontWeight: 'bold' }}>WORKDIR</span> /app
                            </Typography>

                            <Typography sx={{ color: "#82AAFF", mb: 1 }}>
                                <span style={{ color: "#C792EA", fontWeight: 'bold' }}>COPY</span> package*.json ./
                            </Typography>

                            <Typography sx={{ color: "#82AAFF", mb: 1 }}>
                                <span style={{ color: "#C792EA", fontWeight: 'bold' }}>RUN</span> npm install
                            </Typography>

                            <Typography sx={{ color: "#82AAFF", mb: 1 }}>
                                <span style={{ color: "#C792EA", fontWeight: 'bold' }}>COPY</span> . .
                            </Typography>

                            <Typography sx={{ color: "#82AAFF", mb: 1 }}>
                                <span style={{ color: "#C792EA", fontWeight: 'bold' }}>EXPOSE</span> 3000
                            </Typography>

                            <Typography sx={{ color: "#82AAFF" }}>
                                <span style={{ color: "#C792EA", fontWeight: 'bold' }}>CMD</span> ["npm", "start"]
                            </Typography>
                        </TerminalWindow>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Stack spacing={2} justifyContent="center" sx={{ height: '100%' }}>
                            <Box sx={{ p: 3, bgcolor: "rgba(13, 183, 237, 0.05)", borderRadius: 2, border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="h6" fontWeight={800} gutterBottom>Build Command</Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: "#ADDB67", mb: 1 }}>
                                    $ docker build -t my-app .
                                </Typography>
                                <Typography variant="caption" sx={{ color: BRAND.muted }}>
                                    The dot (<code>.</code>) tells Docker to look for the Dockerfile in the current directory.
                                </Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>



                {/* 3. KEY INSTRUCTIONS EXPLAINED */}
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 6, textAlign: "center" }}>
                    The <span style={{ color: DOCKER_BLUE }}>Big 4</span> Instructions
                </Typography>

                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            label: "FROM",
                            icon: <StorageIcon />,
                            desc: "Sets the Base Image (e.g., Ubuntu, Node, Python). Every Dockerfile MUST start with this."
                        },
                        {
                            label: "WORKDIR",
                            icon: <FolderOpenIcon />,
                            desc: "Sets the directory where all subsequent commands will run. Like 'cd' but persistent."
                        },
                        {
                            label: "COPY / ADD",
                            icon: <DescriptionIcon />,
                            desc: "Moves files from your local machine into the image's filesystem."
                        },
                        {
                            label: "RUN",
                            icon: <TerminalIcon />,
                            desc: "Executes commands during the build process (e.g., installing packages)."
                        }
                    ].map((item, i) => (
                        <Grid item xs={12} sm={6} key={i}>
                            <InstructionCard>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                    <Box sx={{ color: DOCKER_BLUE }}>{item.icon}</Box>
                                    <Typography variant="h6" fontWeight={900}>{item.label}</Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>
                                    {item.desc}
                                </Typography>
                            </InstructionCard>
                        </Grid>
                    ))}
                </Grid>

                {/* 4. CMD VS ENTRYPOINT (The Junior Trap) */}
                <Paper sx={{
                    p: 4,
                    bgcolor: "rgba(255, 255, 255, 0.02)",
                    border: `1px solid ${BRAND.border}`,
                    borderRadius: 4
                }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <PlayCircleOutlineIcon sx={{ color: DOCKER_BLUE }} />
                        <Typography variant="h6" fontWeight={800}>CMD vs ENTRYPOINT</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.7 }}>
                        <strong>CMD</strong> is the default command that runs when your container starts. It can be easily
                        overwritten when starting the container. <br/><br/>
                        <strong>ENTRYPOINT</strong> is for commands that should NOT be easily changed, making your
                        container act like an executable tool.
                    </Typography>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Now, let's persist our data with Volumes.</Typography>
                    <Link to="/docker-volumes" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(13, 183, 237, 0.3)`,
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Next: Volumes & Data
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Authorized Content by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
